import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Calendar, MapPin, Newspaper, 
  ChevronLeft, ChevronRight, X, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AboutExpoPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/expo-content`);
      // Merge with hardcoded images to ensure they always show
      const data = response.data;
      if (!data.magazine_images || data.magazine_images.length === 0) {
        data.magazine_images = [
          "https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/yadzstd9_Screenshot%202026-03-14%20131459.jpg",
          "https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/m68sqn7m_Screenshot%202026-03-14%20131518.jpg"
        ];
      }
      if (!data.hero_image) {
        data.hero_image = "https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/1wclnvs3_buytickets2%20copy.jpg";
      }
      if (!data.event_title) {
        data.event_title = "The Gibraltar Wedding Expo";
      }
      if (!data.event_description) {
        data.event_description = "The Gibraltar Wedding Expo was an exclusive event showcasing the finest wedding vendors and services available in Gibraltar. It brought together couples planning their special day with trusted local suppliers, offering inspiration, expert advice, and exclusive deals.";
      }
      if (!data.event_date) {
        data.event_date = "March 18th, 2024";
      }
      if (!data.event_venue) {
        data.event_venue = "Sunborn Gibraltar";
      }
      setContent(data);
    } catch (err) {
      console.error('Failed to fetch expo content:', err);
      // Use hardcoded defaults
      setContent({
        event_title: "The Gibraltar Wedding Expo",
        event_description: "The Gibraltar Wedding Expo was an exclusive event showcasing the finest wedding vendors and services available in Gibraltar. It brought together couples planning their special day with trusted local suppliers, offering inspiration, expert advice, and exclusive deals.",
        event_date: "March 18th, 2024",
        event_venue: "Sunborn Gibraltar",
        hero_image: "https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/1wclnvs3_buytickets2%20copy.jpg",
        magazine_images: [
          "https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/yadzstd9_Screenshot%202026-03-14%20131459.jpg",
          "https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/m68sqn7m_Screenshot%202026-03-14%20131518.jpg"
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const magazineImages = content?.magazine_images || [];

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(magazineImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % magazineImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(magazineImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + magazineImages.length) % magazineImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(magazineImages[newIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F4] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F4]">
      {/* Header */}
      <div className="bg-[#5C0120] text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-serif mb-4">About the Wedding Expo</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Gibraltar's premier wedding showcase bringing together the best vendors and couples
          </p>
        </div>
      </div>

      {/* Event Info Section */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-sm border border-stone-100 p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-serif text-[#5C0120] mb-4">{content?.event_title}</h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                {content?.event_description}
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-stone-600">
                  <Calendar className="h-5 w-5 text-[#D4AF37]" />
                  <span><strong>Date:</strong> {content?.event_date}</span>
                </div>
                <div className="flex items-center gap-3 text-stone-600">
                  <MapPin className="h-5 w-5 text-[#D4AF37]" />
                  <span><strong>Venue:</strong> {content?.event_venue}</span>
                </div>
              </div>
            </div>
            {/* Hero/Event Poster Image */}
            <div className="rounded-sm overflow-hidden">
              {content?.hero_image && (
                <img 
                  src={content.hero_image} 
                  alt="Gibraltar Wedding Expo"
                  className="w-full h-auto object-cover"
                />
              )}
            </div>
          </div>
        </div>

        {/* Magazine Feature Section */}
        {magazineImages.length > 0 && (
          <div className="bg-white rounded-sm border border-stone-100 p-8 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Newspaper className="h-6 w-6 text-[#D4AF37]" />
              <h2 className="text-2xl font-serif text-[#5C0120]">Featured in Globe Magazine</h2>
            </div>
            <p className="text-stone-600 leading-relaxed mb-6">
              The Gibraltar Wedding Expo was proudly featured in Globe Magazine, highlighting the success 
              of our inaugural event and the amazing vendors who participated.
            </p>
            
            <div className="space-y-6">
              {magazineImages.map((image, index) => (
                <div 
                  key={index}
                  className="rounded-sm overflow-hidden cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <img 
                    src={image} 
                    alt={`Globe Magazine feature ${index + 1}`}
                    className="w-full h-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#5C0120] to-[#5C0120]/90 rounded-sm p-8 text-center text-white">
          <h2 className="text-2xl font-serif mb-4">Planning Your Gibraltar Wedding?</h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Browse our directory of trusted wedding vendors who participated in the expo 
            and are ready to help make your special day perfect.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white">
              <Link to="/vendors">Browse Vendors</Link>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/planner">Start Planning</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-stone-300 transition-colors"
            onClick={closeLightbox}
          >
            <X className="h-8 w-8" />
          </button>
          
          {magazineImages.length > 1 && (
            <>
              <button 
                className="absolute left-4 text-white hover:text-stone-300 transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
              >
                <ChevronLeft className="h-10 w-10" />
              </button>
              <button 
                className="absolute right-4 text-white hover:text-stone-300 transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
              >
                <ChevronRight className="h-10 w-10" />
              </button>
            </>
          )}
          
          <div 
            className="max-w-4xl max-h-[80vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedImage} 
              alt="Globe Magazine"
              className="max-w-full max-h-[75vh] object-contain mx-auto"
            />
            <p className="text-stone-400 text-center text-sm mt-4">
              {currentIndex + 1} / {magazineImages.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
